/**
 * Hooks for persisted user notifications (unread list + mark-read).
 *
 * @module useNotifications
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getUnreadNotifications,
  markNotificationsRead,
  type NotificationItem,
} from '@/lib/notificationsApi';

export const UNREAD_NOTIFICATIONS_QUERY_KEY = ['unreadNotifications'] as const;

export function useUnreadNotifications(enabled: boolean = true) {
  const { data, isLoading, isError, refetch } = useQuery<NotificationItem[]>({
    queryKey: UNREAD_NOTIFICATIONS_QUERY_KEY,
    queryFn: getUnreadNotifications,
    enabled,
    staleTime: 0,
    refetchOnWindowFocus: true,
  });
  return { notifications: data ?? [], isLoading, isError, refetch };
}

export function useMarkNotificationsRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (type?: string) => markNotificationsRead(type),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: UNREAD_NOTIFICATIONS_QUERY_KEY });
    },
  });
}

/** Count of unread notifications of a given type (or all when type omitted). */
export function countUnread(notifications: NotificationItem[], type?: string): number {
  if (!type) return notifications.length;
  return notifications.filter((n) => n.type === type).length;
}
